import React, { useRef, useState } from 'react';

import {
  Button,
  ButtonBase,
  Checkbox,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';

export const SEARCH_FROM = 9;
export type PickOption = { value: string; label: React.ReactNode; disabled?: boolean };

type PickPopoverProps = {
  label: string;
  options: Array<PickOption>;
  picked: Array<string>;
  single?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  onPick: (picked: Array<string>) => void;
  children: React.ReactNode;
  overlay?: boolean;
  triggerSx?: SxProps<Theme>;
  empty?: string;
  onOpen?: () => void;
};

export default function PickPopover({
  label,
  options,
  picked,
  single,
  clearable = true,
  disabled,
  onPick,
  children,
  overlay,
  triggerSx,
  empty = 'No match',
  onOpen,
}: PickPopoverProps) {
  const [anchor, setAnchor] = useState<HTMLElement>();
  const [query, setQuery] = useState('');
  const list = useRef<HTMLUListElement>(null);
  const keys = (event: React.KeyboardEvent) => {
    const options = Array.from(
      list.current?.querySelectorAll<HTMLElement>('[role=option]:not([aria-disabled=true])') ?? [],
    );
    const index = options.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      options[Math.max(0, Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
    }
  };
  const close = () => {
    setAnchor(undefined);
    setQuery('');
  };
  const tabOut = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    event.preventDefault();
    const trigger = anchor;
    close();
    trigger?.focus();
  };
  const shown = options.filter((option) => String(option.label).toLowerCase().includes(query.trim().toLowerCase()));
  const choose = (value: string) => {
    onPick(
      single
        ? clearable && picked.includes(value)
          ? []
          : [value]
        : picked.includes(value)
          ? picked.filter((entry) => entry !== value)
          : [...picked, value],
    );
    if (single) close();
  };
  return (
    <>
      <ButtonBase
        aria-expanded={Boolean(anchor)}
        aria-haspopup="listbox"
        aria-label={label}
        disabled={disabled}
        onClick={(event) => {
          setAnchor(event.currentTarget);
          onOpen?.();
        }}
        sx={
          triggerSx ??
          (overlay
            ? { position: 'absolute', inset: 0, borderRadius: 'inherit' }
            : {
                bgcolor: 'action.hover',
                borderRadius: '8px',
                fontSize: 13,
                justifyContent: 'space-between',
                minHeight: 28,
                px: 1,
                textAlign: 'left',
                width: '100%',
              })
        }
      >
        {overlay ? null : children}
      </ButtonBase>
      <Popover
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={close}
        open={Boolean(anchor)}
        slotProps={{ paper: { onKeyDown: tabOut, sx: { minWidth: 220, maxWidth: 360 } } }}
      >
        {options.length >= SEARCH_FROM ? (
          <InputBase
            autoFocus
            fullWidth
            inputProps={{ 'aria-label': `Find ${label}` }}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={keys}
            placeholder="Find"
            sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
            value={query}
          />
        ) : null}
        <List
          aria-label={label}
          aria-multiselectable={!single}
          dense
          onKeyDown={keys}
          ref={list}
          role="listbox"
          sx={{ maxHeight: 300, overflow: 'auto' }}
        >
          {shown.map((option, index) => (
            <ListItemButton
              autoFocus={options.length < SEARCH_FROM && index === 0}
              aria-selected={picked.includes(option.value)}
              disabled={option.disabled}
              key={option.value}
              onClick={() => choose(option.value)}
              role="option"
              sx={{ minHeight: 28 }}
              selected={picked.includes(option.value)}
            >
              {!single ? <Checkbox checked={picked.includes(option.value)} size="small" tabIndex={-1} /> : null}
              <ListItemText primary={option.label} slotProps={{ primary: { fontSize: 13 } }} />
              {!single ? (
                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    onPick([option.value]);
                  }}
                  size="small"
                >
                  Only
                </Button>
              ) : null}
            </ListItemButton>
          ))}
          {!shown.length ? (
            <Typography color="text.secondary" sx={{ p: 1.5 }}>
              {empty}
            </Typography>
          ) : null}
        </List>
        {!single ? (
          <Typography
            color="text.secondary"
            sx={{ borderTop: 1, borderColor: 'divider', fontSize: 11.5, px: 1.5, py: 0.5 }}
          >
            Applies as you tick
          </Typography>
        ) : null}
        {clearable && (picked.length || (!single && query && shown.length)) ? (
          <Stack direction="row" sx={{ borderTop: 1, borderColor: 'divider', px: 1 }}>
            {!single && query && shown.length ? (
              <Button
                onClick={() =>
                  onPick([
                    ...new Set([
                      ...picked,
                      ...shown.filter((option) => !option.disabled).map((option) => option.value),
                    ]),
                  ])
                }
                size="small"
              >
                Select {shown.length} shown
              </Button>
            ) : null}
            {picked.length ? (
              <Button
                onClick={() => {
                  onPick([]);
                  if (single) close();
                }}
                size="small"
              >
                Clear
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Popover>
    </>
  );
}
